import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { VoiceCatalogService } from './voice-catalog.service';
import { VoiceCatalogListItem } from './voice-catalog.types';

interface LanguageChoice {
  value: string;
  experimental: boolean;
}

/**
 * Onboarding voice audition (AIVA-2150) — lets a user hear a candidate voice
 * before it's saved to the bot. Deliberately self-contained (not wired into
 * botSetupForm): auditioning is ephemeral and never part of the submitted
 * bot config, unlike the sibling ChatbotCustomizationComponent.
 *
 * The AIVA1/AIVA2 gate lives server-side (VoiceCatalogService/VoiceAuditionService
 * in ai-engine): an AIVA1 bot's catalog call simply returns an empty list, so
 * this component needs no flow-awareness of its own — it just renders what
 * comes back.
 *
 * Language choice is deliberately NOT a free picker: it's the intersection of
 * the bot's OWN configured language(s) (`languages` input, from the wizard's
 * default-language/allowed-languages fields) and each candidate voice's
 * verified/experimental languages — auditioning a language the bot can't
 * even speak would be misleading.
 */
@Component({
  selector: 'app-voice-catalog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSnackBarModule,
    TranslateModule,
  ],
  templateUrl: './voice-catalog.component.html',
  styleUrls: ['./voice-catalog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VoiceCatalogComponent implements OnChanges {
  @Input({ required: true }) botId!: string | undefined;
  /**
   * Language codes the bot is configured to speak (e.g. ['en-US', 'es-MX']),
   * default language first — each row's language dropdown pre-selects its
   * first compatible entry, so the default language is chosen by default.
   */
  @Input() languages: string[] = [];

  @ViewChild('voiceAudioPlayer') private readonly audioRef!: ElementRef<HTMLAudioElement>;

  private readonly _voiceCatalogService = inject(VoiceCatalogService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  /** The in-flight catalog load, cancelled when a newer botId arrives. */
  private catalogLoad?: Subscription;

  voices: VoiceCatalogListItem[] = [];
  loading = false;
  loadedForBotId: string | null = null;
  auditioningKey: string | null = null;
  playingKey: string | null = null;
  selectingKey: string | null = null;
  /**
   * language -> voice.key currently selected for it. Seeded from each
   * candidate's `selectedLanguages` (the bot's real, persisted
   * settings.voice.providers, computed server-side) whenever the catalog
   * loads, then kept in sync locally after a successful select() — so
   * "Selected" survives reopening the page, not just the current session.
   */
  selectedVoiceKeyByLanguage: Record<string, string> = {};

  /** voice.key -> currently selected language for that row's audition. */
  selectedLanguage: Record<string, string> = {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['botId'] && this.botId && this.botId !== this.loadedForBotId) {
      this.loadCatalog(this.botId);
      return;
    }
    if (changes['languages'] && this.voices.length) {
      this.resetLanguageSelections();
    }
  }

  private loadCatalog(botId: string): void {
    this.loading = true;
    this.voices = [];
    this.selectedVoiceKeyByLanguage = {};
    this.catalogLoad?.unsubscribe();
    this.catalogLoad = this._voiceCatalogService
      .listCatalog(botId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (voices) => {
          this.voices = voices;
          this.loadedForBotId = botId;
          this.loading = false;
          for (const voice of voices) {
            for (const language of voice.selectedLanguages ?? []) {
              this.selectedVoiceKeyByLanguage[language] = voice.key;
            }
          }
          this.resetLanguageSelections();
          this.cd.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.loadedForBotId = botId;
          this.cd.detectChanges();
        },
      });
  }

  private resetLanguageSelections(): void {
    this.selectedLanguage = {};
    for (const voice of this.voices) {
      const choices = this.languageChoicesFor(voice);
      if (choices.length) this.selectedLanguage[voice.key] = choices[0].value;
    }
  }

  /** Languages this candidate can be auditioned in, given what the bot itself speaks. */
  languageChoicesFor(voice: VoiceCatalogListItem): LanguageChoice[] {
    const verified = new Set(voice.verifiedLanguages ?? []);
    const experimental = new Set(voice.experimentalLanguages ?? []);
    return this.languages
      .filter(lang => verified.has(lang) || experimental.has(lang))
      .map(lang => ({ value: lang, experimental: !verified.has(lang) }));
  }

  onLanguageChange(voiceKey: string, language: string): void {
    this.selectedLanguage[voiceKey] = language;
  }

  audition(voice: VoiceCatalogListItem): void {
    const language = this.selectedLanguage[voice.key];
    if (!this.botId || !voice.available || !language || this.auditioningKey) return;

    this.auditioningKey = voice.key;
    this._voiceCatalogService
      .audition(this.botId, voice.key, { language })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.auditioningKey = null;
          this.playClip(result.audioBase64, result.mime, voice.key);
          if (!result.verified) {
            this._snackBar.open('This language has not been verified for this voice yet.', 'Dismiss', {
              duration: 4000,
            });
          }
          this.cd.detectChanges();
        },
        error: (err) => {
          this.auditioningKey = null;
          const message = err?.error?.message || 'Could not render this voice sample.';
          this._snackBar.open(message, 'Dismiss', { duration: 5000 });
          this.cd.detectChanges();
        },
      });
  }

  /** True when this voice is the one most recently selected, for its current language choice. */
  isSelected(voice: VoiceCatalogListItem): boolean {
    const language = this.selectedLanguage[voice.key];
    return !!language && this.selectedVoiceKeyByLanguage[language] === voice.key;
  }

  select(voice: VoiceCatalogListItem): void {
    const language = this.selectedLanguage[voice.key];
    if (!this.botId || !voice.available || !language || this.selectingKey) return;

    this.selectingKey = voice.key;
    const botId = this.botId;
    this._voiceCatalogService
      .select(botId, voice.key, { language })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.selectingKey = null;
          // The panel switched to another bot while this save was in flight.
          if (this.botId !== botId) return;
          this.selectedVoiceKeyByLanguage[result.language] = result.voiceKey;
          this._snackBar.open(`${result.name} is now this bot's voice for ${result.language}.`, 'Dismiss', {
            duration: 4000,
          });
          this.cd.detectChanges();
        },
        error: (err) => {
          this.selectingKey = null;
          const message = err?.error?.message || 'Could not save this voice.';
          this._snackBar.open(message, 'Dismiss', { duration: 5000 });
          this.cd.detectChanges();
        },
      });
  }

  private playClip(audioBase64: string, mime: string, key: string): void {
    const audioEl = this.audioRef?.nativeElement;
    if (!audioEl) return;

    const stop = (): void => {
      this.playingKey = null;
      this.cd.detectChanges();
    };
    audioEl.onended = stop;
    audioEl.onerror = stop;
    audioEl.src = `data:${mime};base64,${audioBase64}`;
    audioEl
      .play()
      .then(() => {
        this.playingKey = key;
        this.cd.detectChanges();
      })
      .catch(stop);
  }

  get premiumVoices(): VoiceCatalogListItem[] {
    return this.voices.filter(v => v.tier === 'premium');
  }

  get budgetVoices(): VoiceCatalogListItem[] {
    return this.voices.filter(v => v.tier === 'budget');
  }
}
